import React, { useState, useEffect } from "react";
import { z } from "zod";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import type { State, County } from "../../../shared/types/models/location";

interface PropertyIndexSearchProps {
  state: State | null;
  county: County | null;
  initialReference?: string;
  initialReference2To?: string;
  onSearch: (values: { reference: string; reference2To?: string }) => void;
  loading?: boolean;
}

const PropertyIndexSearch: React.FC<PropertyIndexSearchProps> = ({
  state,
  county,
  initialReference = "",
  initialReference2To = "",
  onSearch,
  loading = false,
}) => {
  const [reference, setReference] = useState(initialReference);
  const [reference2To, setReference2To] = useState(initialReference2To);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Safely parse documentSchema which might be a JSON string or an object
  const parsedSchema = typeof county?.documentSchema === 'string' 
    ? (() => { try { return JSON.parse(county.documentSchema); } catch { return null; } })() 
    : county?.documentSchema;

  // Derive dynamic labels from the county's documentSchema.indexes (V3 Schema-Driven UI)
  const index1Label = parsedSchema?.indexes?.[0]?.label || "Reference";
  const index2Label = parsedSchema?.indexes?.[1]?.label || null;

  const getDynamicSchema = () => {
    let schema = z.object({
      reference: z.string().optional(),
      reference2To: z.string().optional()
    });
    const req1 = parsedSchema?.indexes?.[0]?.required;
    const index1Required = req1 !== false && String(req1).toLowerCase() !== "false" && req1 !== 0;
    
    const req2 = parsedSchema?.indexes?.[1]?.required;
    const index2Required = req2 !== false && String(req2).toLowerCase() !== "false" && req2 !== 0;

    if (index1Required) {
      schema = schema.extend({
        reference: z.string().min(1, `${index1Label} is required`)
      });
    }

    if (index2Label && index2Label.trim().length > 0) {
      if (index2Required) {
        schema = schema.extend({
          reference2To: z.string().min(1, `${index2Label} is required`)
        });
      }
    }
    return schema;
  };

  const isFormValid = !!state && !!county && getDynamicSchema().safeParse({ reference, reference2To }).success;

  const validateField = (field: 'reference' | 'reference2To', value: string) => {
    const fieldSchema = getDynamicSchema().shape[field];
    const result = fieldSchema.safeParse(value);
    return result.success ? undefined : result.error.errors[0].message;
  };

  const handleBlur = (field: 'reference' | 'reference2To', value: string) => {
    setTouched(t => ({ ...t, [field]: true }));
    const error = validateField(field, value);
    if (error) {
      setErrors(e => ({ ...e, [field]: error }));
    } else {
      setErrors(e => {
        const newE = { ...e };
        delete newE[field];
        return newE;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ reference: true, reference2To: true });
    
    if (!state || !county) return;

    const schema = getDynamicSchema();
    const result = schema.safeParse({ reference, reference2To });
    
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    onSearch({ reference, reference2To: index2Label ? reference2To : undefined });
  };

  const label1 = index1Label;

  return (
    <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit} noValidate autoComplete="off">
      {!county && (
        <div className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta italic text-center">
          Please select a State and County above to search properties.
        </div>
      )}

      
      {/* Dynamic Reference 1 Input */}
      <div className="flex flex-col gap-1 w-full">
        <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta flex items-center gap-1 relative" htmlFor="reference-input">
          {label1}
          <span className="relative group cursor-pointer">
            <InformationCircleIcon className="w-4 h-4 text-[#0B4BB6]" />
            <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-white border border-[#D7DFEC] rounded shadow-lg px-3 py-2 text-xs text-[#485D7D] w-64 font-normal font-plus-jakarta">
              Enter the {label1}
            </span>
          </span>
        </label>
        <input
          id="reference-input"
          name="fzd_reference_input_1"
          type="text"
          autoComplete="new-password"
          value={reference}
          onChange={e => setReference(e.target.value)}
          onBlur={e => handleBlur('reference', e.target.value)}
          className={`w-full px-3 py-2 rounded border ${errors.reference ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-semibold font-plus-jakarta focus:outline-none`}
          placeholder={label1}
          aria-invalid={!!errors.reference}
          aria-describedby={errors.reference ? "reference-error" : undefined}
          data-testid="reference-input"
        />
        {(touched.reference || touched.reference === undefined) && errors.reference && <span id="reference-error" className="text-red-500 text-xs mt-0.5" role="alert">{errors.reference}</span>}
      </div>

      {/* Dynamic Reference 2 Input */}
      {index2Label && index2Label.trim().length > 0 && (
        <div className="flex flex-col gap-1 w-full mt-2">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta flex items-center gap-1 relative" htmlFor="reference2-input">
            {index2Label}
            <span className="relative group cursor-pointer">
              <InformationCircleIcon className="w-4 h-4 text-[#0B4BB6]" />
              <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-white border border-[#D7DFEC] rounded shadow-lg px-3 py-2 text-xs text-[#485D7D] w-64 font-normal font-plus-jakarta">
                Enter the {index2Label}
              </span>
            </span>
          </label>
          <input
            id="reference2-input"
            name="fzd_reference_input_2"
            type="text"
            autoComplete="new-password"
            value={reference2To}
            onChange={e => setReference2To(e.target.value)}
            onBlur={e => handleBlur('reference2To', e.target.value)}
            className={`w-full px-3 py-2 rounded border ${errors.reference2To ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-semibold font-plus-jakarta focus:outline-none`}
            placeholder={index2Label}
            aria-invalid={!!errors.reference2To}
            aria-describedby={errors.reference2To ? "reference2-error" : undefined}
            data-testid="reference2-input"
          />
          {(touched.reference2To || touched.reference2To === undefined) && errors.reference2To && <span id="reference2-error" className="text-red-500 text-xs mt-0.5" role="alert">{errors.reference2To}</span>}
        </div>
      )}

      {/* Search Button */}
      <button
        type="submit"
        className={`w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2 rounded-full mt-2 hover:bg-[#23235a] transition-colors border-2 border-[#0B4BB6] ${(!isFormValid || loading) ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={!isFormValid || loading}
        aria-disabled={!isFormValid || loading}
        data-testid="search-button"
      >
        {loading ? 'Searching...' : 'Search'}
      </button>
    </form>
  );
};

export default PropertyIndexSearch; 